import { subCenter, radiusForWidth, boardSize } from '../../utils/hexGeometry'
import { tileCenter, laneGeometry } from './corridors'

// The war scene, as a flat list of things to draw, computed ONLY from the
// rules module's output (a fixture's `result`): Gate 1 item D. Every entry is
//   { key, kind, side, s, x, y, tex, state }
// key    stable identity: kind:id:side:cell (verify.js matches on it)
// x, y   board coordinates, from the lab's own hex geometry (utils/hexGeometry)
// tex    which texture to draw it with (scene/atlas.js)
// state  what the sprite shows beyond its identity (impact, stamp text, dead…)
//
// p1 is drawn at the bottom ("self"), p2 on top ("opp"), as in the lab. The
// view shows everything on both Hives (spectator view, the heaviest case).

export const SEATS = [['p1', 'self'], ['p2', 'opp']]
// Draw order, bottom to top.
export const LAYERS = ['cell', 'scan', 'crater', 'lane', 'structure', 'brood', 'egg', 'rocket', 'drone', 'stamp']

export function layoutFor(width, height) {
    const unit = boardSize(1)
    const R = Math.min(radiusForWidth(width), height / unit.height)
    const size = boardSize(R)
    return { R, width: size.width, height: size.height, offsetX: (width - size.width) / 2, offsetY: (height - size.height) / 2 }
}

// Cell terrain: one of LOAD.cellTerrainVariants textures, mixed across the
// cells (half the variants for each Hive, so the two Hives still read apart).
export function terrainOf(side, s, variants = 8) {
    let h = (Math.imul(s + 1, 2654435761) ^ (side === 'opp' ? 0x9e37 : 0x7f4a)) >>> 0
    h = (h ^ (h >>> 15)) >>> 0
    const half = variants / 2
    return `terrain-${(h % half) + (side === 'opp' ? half : 0)}`
}

// Structure library: each structure gets its own texture, spread across the
// library's atlases (64 per atlas, entries 0..total-1): structure k goes to
// atlas k % atlases.
export function libraryTexOf(k, { total = 150, perAtlas = 64 } = {}) {
    const atlases = Math.ceil(total / perAtlas)
    const atlas = k % atlases
    const inAtlas = Math.min(perAtlas, total - atlas * perAtlas)
    const slot = (Math.floor(k / atlases) * 3) % inAtlas
    return `lib:${atlas * perAtlas + slot}`
}

export function buildSceneModel(result, R) {
    const items = []
    let structureIndex = 0
    const at = (side, s) => subCenter(side, s, R)
    for (const [seat, side] of SEATS) {
        const hf = result[seat]?.homefront
        if (!hf) continue
        hf.subplots.forEach((sp, s) => {
            const { x, y } = at(side, s)
            items.push({ key: `cell:${side}:${s}`, kind: 'cell', side, s, x, y, tex: terrainOf(side, s), state: '' })
            if (sp.scanned) items.push({ key: `scan:${side}:${s}`, kind: 'scan', side, s, x, y, tex: sp.scanned === 'aged' ? 'scan-aged' : 'scan', state: String(sp.scanned) })
            if (sp.destroyed) items.push({ key: `crater:${side}:${s}`, kind: 'crater', side, s, x, y, tex: sp.impact ? 'crater-impact' : 'crater', state: sp.impact ? 'impact' : '' })
            if (sp.distance != null) {
                const text = `${sp.distanceLetter ?? ''}:${sp.distance}`
                items.push({ key: `stamp:${side}:${s}`, kind: 'stamp', side, s, x, y, tex: `${sp.distanceDead ? 'stamp-dead' : 'stamp'}:${text}`, state: `${text}${sp.distanceDead ? ' dead' : ''}` })
            }
        })
        // Broods: their own map since rules v8 (older fixtures keep them among
        // the structures as type 'base').
        for (const st of [...Object.values(hf.structures), ...Object.values(hf.broods ?? {})]) {
            const brood = st.type === 'base' || !!hf.broods?.[st.id]
            const libTex = brood ? null : libraryTexOf(structureIndex++)
            st.footprint.forEach((s, i) => {
                const { x, y } = at(side, s)
                const dead = !!hf.subplots[s]?.destroyed
                if (brood) {
                    const role = i === 0 ? 'nest' : 'drone'
                    items.push({ key: `brood:${st.id}:${side}:${s}`, kind: 'brood', side, s, x, y, tex: st.underConstruction ? 'brood-building' : `brood-${role}`, state: `${role}${st.underConstruction ? ' building' : ''}${dead ? ' dead' : ''}` })
                } else {
                    items.push({ key: `structure:${st.id}:${side}:${s}`, kind: 'structure', side, s, x, y, tex: dead ? 'structure-dead' : libTex, state: dead ? 'dead' : '' })
                }
            })
            if (brood && st.egg === 'egg') {
                const s = st.footprint[0]
                const { x, y } = at(side, s)
                items.push({ key: `egg:${st.id}:${side}:${s}`, kind: 'egg', side, s, x, y, tex: 'egg', state: '' })
            }
            ;(st.rocketCells ?? []).forEach((s, i) => {
                const { x, y } = at(side, s)
                items.push({ key: `rocket:${st.id}:${side}:${s}:${i}`, kind: 'rocket', side, s, x, y, tex: 'rocket', state: '' })
            })
        }
    }
    // Item A: the corridors, both sides, from each side's board (rules
    // output): a capacity lane per corridor, a drone tile per occupied slot.
    for (const [seat, side] of SEATS) {
        const board = result[seat]?.board
        if (!board) continue
        board.forEach((bf, c) => {
            const cap = bf.maxArmyQty ?? 0
            if (cap > 0) {
                const g = laneGeometry(side, c, cap, R)
                items.push({ key: `lane:${side}:${c}`, kind: 'lane', side, s: null, x: g.x, y: g.y, tex: `lane-${cap}`, state: String(cap) })
            }
            ;(bf.slots ?? []).forEach((tok, p) => {
                if (!tok) return
                const { x, y } = tileCenter(side, c, p, R)
                items.push({ key: `drone:${side}:${c}:${p}`, kind: 'drone', side, s: null, x, y, tex: `drone-${tok.kind}`, state: `${tok.kind}${tok.delivered ? ' delivered' : ''}` })
            })
        })
    }
    return items
}
